import { useState, useEffect } from 'react';

import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '@mui/material';
import AppUpdateProgressDialog from './components/AppUpdateProgressDialog/AppUpdateProgressDialog';

import Sidebar from './components/Sidebar/Sidebar';
import Topbar from './components/Sidebar/Topbar';

import Login from './pages/login/Login';
import Dashboard from './pages/dashboard/Dashboard';
import Clients from './pages/clients/Clients';
import Stocks from './pages/location_management/stock_movements/Stocks';
import Sales from './pages/location/sales/Sales';
import InventoryProducts from './pages/location/products/Products';
import InventoryPreparation from './pages/location/preparation/Preparation';
import InventorySupplies from './pages/location/supplies/Supplies';
import Products from './pages/production/products/Products';
import Preparation from './pages/production/preparation/Preparation';
import Supplies from './pages/production/supplies/Supplies';
import Batches from './pages/production/batches/Batches';
import PlantLots from './pages/production/lots/PlantLots';
import Resources from './pages/production/resources/Resources';
import StaffProduction from './pages/production/staff/StaffProduction';
import Plant from './pages/location_management/plant/Plant';
import Suppliers from './pages/suppliers/Suppliers';
import ExpensesPage from './pages/expenses/ExpensesPage';
import ProfilePage from './pages/settings/profile/ProfilePage';
import SupportPage from './pages/support/SupportPage';
import ObjectivesPage from './pages/settings/objectives/ObjectivesPage';
import TasksPage from './pages/tasks/TasksPage';
import TablesPage from './pages/tables/TablesPage';
import StockCountPage from './pages/stock_count/daily/StockCountPage';
import StockCountManagementPage from './pages/stock_count/management/StockCountManagementPage';
import TransactionsPage from './pages/location/transactions/TransactionsPage';
import AllTransactionsPage from './pages/location_management/all_transactions/TransactionsPage';
import Security from './pages/settings/security/Security';
import DiscountPage from './pages/location/discount/DiscountPage';
import BranchReports from './pages/reports/branch/BranchReports';
import ClientsReports from './pages/reports/clients/ClientsReports';
import CostReports from './pages/reports/cost/CostReports';
import InventoryReports from './pages/reports/inventory/InventoryReports';
import CostAlertsReports from './pages/reports/costAlerts/CostAlertsReports';
import AssistantPage from './pages/assistant/AssistantPage';
import SummaryReports from './pages/reports/summary/SummaryReports';
import SalesReports from './pages/reports/sales/SalesReports';
import ProductsReports from './pages/reports/products/ProductsReports';
import ProfitabilityReports from './pages/reports/profitability/ProfitabilityReports';
import Locations from './pages/location_management/locations/Locations';
import CashRegisterPage from './pages/location_management/open_and_close_cash/CashRegisterPage';
import StaffLocation from './pages/location/staff/StaffLocation';
import RolesPage from './pages/settings/roles/RolesPage';
import StaffPage from './pages/location_management/staff/StaffPage';
import LogoutPage from './pages/logout/LogoutPage';
import CashRegisterByUserPage from './pages/location/cash_register/CashRegisterByUserPage';
import SettingsPage from './pages/settings/settings/SettingsPage';
import B2BClients from './pages/b2b/clients/B2BClients';
import B2BCommercialProfiles from './pages/b2b/commercial_profiles/B2BCommercialProfiles';
import B2BOrders from './pages/b2b/orders/B2BOrders';
import AllOrdersPage from './pages/b2b/billing_statment/AllOrdersPage';
import B2BProforma from './pages/b2b/proforma/B2BProfroma';
import BillingStatement from './pages/b2b/billing_statment/BillingStatement';
import PaymentsBenefitsPage from './pages/payments/PaymentsBenefitsPage';
import LearningPage from './pages/learning/LearningPage';
import AccountingPage from './pages/accounting/AccountingPage';
import CreateInvoice from './pages/billing/create/CreateInvoice';
import ReceivedInvoice from './pages/billing/received/ReceivedInvoice';
import SendInvoice from './pages/billing/send/SendInvoice';
import SumaryInvoice from './pages/billing/summary/SumaryInvoice';
import Emails from './pages/marketing/Emails';
import Promotions from './pages/marketing/Promotions';
import Campaigns from './pages/marketing/Campaigns';
import Planning from './pages/marketing/Planning';
import Sites from './pages/sites/Sites';
import Publish from './pages/sites/publish/Publish';
import Notifications from './pages/sites/notifications/Notifications';
import AllNotifications from './pages/notifications/AllNotifications';
import SiteProducts from './pages/sites/products/Products';
import SiteOrders from './pages/sites/orders/Orders';
import NotFound from './pages/error_pages/NotFound';
import PermissionDenied from './pages/error_pages/PermissionDenied';
import InternalServerError from './pages/error_pages/InternalServerError';
import PrivateRoute from './components/Route/PrivateRoute';
import PublicRoute from './components/Route/PublicRoute';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CashRegisterProvider } from "./context/CashRegisterContext";
import { AuthProvider } from './context/AuthContext';
import { MenuProvider } from './context/MenuContext';
import { PluginsProvider } from './context/PluginsContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { NavigationProvider } from './context/NavigationContext';
import useDisableNumberInputScroll from './hooks/useDisableNumberInputScroll';
import { setNavigateRef } from './utils/NavigationService';
import PlatformBridge from './utils/PlatformBridge';
import { getDefaultHomePath } from './utils/HomeNavigation';
import { canAccessAccounting } from './utils/accountingAccess';

import './assets/scss/main.scss';

const DefaultHomeRedirect = () => {
  return <Navigate to={getDefaultHomePath()} replace />;
};

const AccountingRoute = ({ children }) => {
  const roleName = localStorage.getItem('role') || '';
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');

  if (!canAccessAccounting({ roleName, permissions })) {
    return <Navigate to="/permission-denied" replace />;
  }

  return children;
};

const SuperAdminRoute = ({ children }) => {
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';

  if (!isSuperAdmin) {
    return <Navigate to="/permission-denied" replace />;
  }

  return children;
};

const SettingsRoute = ({ children }) => {
  const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');

  if (!isSuperAdmin && !permissions.map(Number).includes(802)) {
    return <Navigate to="/permission-denied" replace />;
  }

  return children;
};

// Oculta rutas de un plugin apagado por la empresa (ver Settings > Plugins).
// Lee de localStorage (mismo patrón que los guards de arriba) en vez de un
// context, para no depender del orden de providers. Ausencia de estado para
// el plugin = habilitado (mismo default que el backend), para no bloquear
// nada antes de que termine de cargar el primer GET /plugins.
const PluginRoute = ({ pluginKey, children }) => {
  const plugins = JSON.parse(localStorage.getItem('plugins') || '[]');
  const plugin = plugins.find((item) => item.key === pluginKey);
  const enabled = plugin ? plugin.enabled !== false : true;

  if (!enabled) {
    return <Navigate to="/permission-denied" replace />;
  }

  return children;
};

const MainLayout = () => {
  const isMobile = useMediaQuery('(max-width:1000px)');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setNavigateRef(navigate);
  }, [navigate]);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleSidebarToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className='flex'>
      <Topbar isMobile={isMobile} isCollapsed={isMobile ? false : isCollapsed} onToggleSidebar={isMobile ? handleDrawerToggle : handleSidebarToggle} />

      {/* Sidebar */}
      <Sidebar 
        isMobile={isMobile} 
        drawerOpen={drawerOpen} 
        onDrawerToggle={handleDrawerToggle} 
        isCollapsed={isMobile ? false : isCollapsed} 
        onToggleSidebar={handleSidebarToggle}
      />

      {/* Contenido Principal */}
      <div className={`content fullscreen`}>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/assistant" element={<PluginRoute pluginKey="ai_assistant"><AssistantPage /></PluginRoute>} />
          <Route path="/tasks" element={<PluginRoute pluginKey="staff_tasks"><TasksPage /></PluginRoute>} />
          <Route path="/tables/:locationId" element={<PluginRoute pluginKey="tables"><TablesPage /></PluginRoute>} />
          <Route path="/stock-count/:locationId" element={<PluginRoute pluginKey="stock_count"><StockCountPage /></PluginRoute>} />
          <Route path="/stock-count-management" element={<PluginRoute pluginKey="stock_count"><StockCountManagementPage /></PluginRoute>} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/stocks" element={<Stocks />} />
          <Route path="/staff-assignment/:locationId" element={<StaffLocation />} />
          <Route path="/staff-production/:locationId" element={<PluginRoute pluginKey="production"><StaffProduction /></PluginRoute>} />
          <Route path="/sales/:locationId" element={<Sales />} />
          <Route path="/cash-register/:locationId" element={<CashRegisterByUserPage />} />
          <Route path="/inventory_supplies/:locationId" element={<PluginRoute pluginKey="food_operations"><InventorySupplies /></PluginRoute>} />
          <Route path="/inventory_preparations/:locationId" element={<PluginRoute pluginKey="food_operations"><InventoryPreparation /></PluginRoute>} />
          <Route path="/inventory_products/:locationId" element={<InventoryProducts />} />            
          <Route path="/products/:locationId" element={<Products />} />
          <Route path="/preparations/:locationId" element={<PluginRoute pluginKey="food_operations"><Preparation /></PluginRoute>} />
          <Route path="/plant" element={<Plant />} />
          <Route path="/supplies/:locationId" element={<PluginRoute pluginKey="food_operations"><Supplies /></PluginRoute>} />
          <Route path="/batches/:locationId" element={<PluginRoute pluginKey="production"><Batches /></PluginRoute>} />
          <Route path="/plant-lots/:locationId" element={<PluginRoute pluginKey="production"><PlantLots /></PluginRoute>} />
          <Route path="/suppliers" element={<Suppliers />} />
          <Route path="/expenses" element={<ExpensesPage />} />
          <Route path="/cost-alerts-reports" element={<CostAlertsReports />} />
          <Route path="/summary-reports" element={<SummaryReports />} />
          <Route path="/sales-reports" element={<SalesReports />} />
          <Route path="/products-reports" element={<ProductsReports />} />
          <Route path="/inventory-reports" element={<InventoryReports />} />
          <Route path="/cost-reports" element={<CostReports />} />
          <Route path="/profitability-reports" element={<ProfitabilityReports />} />
          <Route path="/clients-reports" element={<ClientsReports />} />
          <Route path="/branch-reports" element={<BranchReports />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/learning" element={<LearningPage />} />
          <Route path="/objectives" element={<ObjectivesPage />} />
          <Route path="/security" element={<Security />} />
          <Route path="/roles" element={<SuperAdminRoute><RolesPage /></SuperAdminRoute>} />
          <Route path="/settings" element={<SettingsRoute><SettingsPage /></SettingsRoute>} />
          <Route path="/open-and-close-cash" element={<CashRegisterPage />} />
          <Route path="/resources/:locationId" element={<PluginRoute pluginKey="production"><Resources /></PluginRoute>} />
          <Route path="/discounts/:locationId" element={<DiscountPage />} />
          <Route path="/view-all-transactions" element={<AllTransactionsPage />} />
          <Route path="/view-transactions/:locationId" element={<TransactionsPage />} />
          <Route path="/b2b/clients" element={<PluginRoute pluginKey="b2b"><B2BClients /></PluginRoute>} />
          <Route path="/b2b/commercial-profiles" element={<PluginRoute pluginKey="b2b"><B2BCommercialProfiles /></PluginRoute>} />
          <Route path="/b2b/proforma" element={<PluginRoute pluginKey="b2b"><B2BProforma /></PluginRoute>} />
          <Route path="/b2b/orders" element={<PluginRoute pluginKey="b2b"><B2BOrders /></PluginRoute>} />
          <Route path="/b2b/billing-statements/:clientId/billing-statements" element={<PluginRoute pluginKey="b2b"><AllOrdersPage /></PluginRoute>} />
          <Route path="/b2b/billing-statement" element={<PluginRoute pluginKey="b2b"><BillingStatement /></PluginRoute>} />
          <Route path="/payments-benefits" element={<PaymentsBenefitsPage />} />
          <Route path="/accounting" element={<AccountingRoute><AccountingPage /></AccountingRoute>} />
          <Route path="/marketing/emails" element={<PluginRoute pluginKey="marketing"><Emails /></PluginRoute>} />
          <Route path="/marketing/planning" element={<PluginRoute pluginKey="marketing"><Planning /></PluginRoute>} />
          <Route path="/marketing/promotions" element={<PluginRoute pluginKey="marketing"><Promotions /></PluginRoute>} />
          <Route path="/marketing/campaigns" element={<PluginRoute pluginKey="marketing"><Planning /></PluginRoute>} />
          <Route path="/marketing/loyalty" element={<PluginRoute pluginKey="marketing"><Campaigns /></PluginRoute>} />
          <Route path="/site/settings" element={<PluginRoute pluginKey="ecommerce"><Sites /></PluginRoute>} />
          <Route path="/site/publish" element={<PluginRoute pluginKey="ecommerce"><Publish /></PluginRoute>} />
          <Route path="/site/notifications" element={<PluginRoute pluginKey="ecommerce"><Notifications /></PluginRoute>} />
          <Route path="/notifications" element={<AllNotifications />} />
          <Route path="/site/products" element={<PluginRoute pluginKey="ecommerce"><SiteProducts /></PluginRoute>} />
          <Route path="/site/orders" element={<PluginRoute pluginKey="ecommerce"><SiteOrders /></PluginRoute>} />
          <Route path="/create_invoice" element={<CreateInvoice />} />
          <Route path="/send_invoice" element={<SendInvoice />} />
          <Route path="/received_invoice" element={<ReceivedInvoice />} />
          <Route path="/summary_invoice" element={<SumaryInvoice />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="*" element={<Navigate to="/not-found" replace />} />
        </Routes>
      </div>
    </div>
  );
};

function App() {
  const [appUpdateStatus, setAppUpdateStatus] = useState(null);

  useDisableNumberInputScroll();

  useEffect(() => {
    const unsubscribe = PlatformBridge.onAppUpdateStatus((payload) => {
      setAppUpdateStatus(payload);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const handleInstallUpdate = async () => {
    await PlatformBridge.installAppUpdate();
  };

  return (
    <AuthProvider>
      <WebSocketProvider>
      <MenuProvider>
      <PluginsProvider>
      <CurrencyProvider>
      <Router basename="/">
          <NavigationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CashRegisterProvider>
                <Routes>
                  <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                  <Route path="/" element={<DefaultHomeRedirect />} />
                  <Route path="/permission-denied" element={<PermissionDenied />} />
                  <Route path="/internal-server-error" element={<InternalServerError />} />
                  <Route path="/not-found" element={<NotFound />} />
                  <Route path="*" element={<PrivateRoute><MainLayout /></PrivateRoute>} />
                </Routes>
                <AppUpdateProgressDialog status={appUpdateStatus} onInstall={handleInstallUpdate} />
              </CashRegisterProvider>
            </LocalizationProvider>
          </NavigationProvider>
        </Router>
      </CurrencyProvider>
      </PluginsProvider>
      </MenuProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
}

export default App;
